import { useMemo, useRef } from 'react'
import { isEqual } from 'lodash-es'
import { firstBy } from 'thenby'
import {
    Channel,
    DmAndGdmModel,
    useDmsAndGdms,
    useDmsAndGdmsUnreads,
    useMutedStreamIds,
    useMyChannels,
    useSpaceDataWithId,
    useSpaceMembers,
    useSpaceMentions,
    useSpaceUnreads,
    useUserLookupStore,
} from 'use-towns-client'

import { useFavoriteChannels } from './useFavoriteChannels'
import { useSpaceChannels } from './useSpaceChannels'

type Params = { spaceId?: string; currentRouteId?: string }

export type ChannelMenuItem = {
    type: 'channel'
    id: string
    channel: Channel
    joined: boolean
    label: string
    latestMs: number
    mentionCount: number
    search: string
    unread: boolean
    favorite: boolean
    muted: boolean
}

export type DMChannelMenuItem = {
    type: 'dm'
    channel: DmAndGdmModel
    id: string
    isGroup: boolean
    label: string
    latestMs: number
    search: string
    unread: boolean
    favorite: boolean
    muted: boolean
}

export type MixedChannelMenuItem = ChannelMenuItem | DMChannelMenuItem

/**
 * maps channel and dm data to a unified format for use in the channel menu
 */
export const useSortedChannels = ({ spaceId, currentRouteId }: Params) => {
    const { spaceUnreadChannelIds: _unreadChannelIds } = useSpaceUnreads()
    const { dmsAndGdmsUnreadIds: _dmUnreadChannelIds } = useDmsAndGdmsUnreads()
    const { dmsAndGdms: _dmChannels } = useDmsAndGdms()
    const spaceMentions = useSpaceMentions(spaceId)
    const channels = useSpaceChannels()
    const { memberIds: spaceMemberIds } = useSpaceMembers()
    const { joinedChannels } = useJoinedChannels(spaceId)
    const { favoriteChannelIds } = useFavoriteChannels()
    const mutedStreamIds = useMutedStreamIds()

    const unreadChannelIds = useMemo(
        () => (spaceId ? _unreadChannelIds[spaceId] : new Set()),
        [spaceId, _unreadChannelIds],
    )

    // - - - - - - - - - - - - - - - - - - - - - - - - - collect and map channels

    const channelItems: ChannelMenuItem[] = useMemo(() => {
        return channels
            .map((channel) => {
                const mentionCount = spaceMentions.reduce(
                    (count, m) =>
                        m.unread && !m.thread && m.channelId === channel.id ? count + 1 : count,
                    0,
                )
                return {
                    type: 'channel',
                    id: channel.id,
                    label: channel.label,
                    search: channel.label,
                    channel,
                    mentionCount,
                    unread: !!unreadChannelIds?.has(channel.id),
                    joined: !!joinedChannels?.has(channel.id),
                    latestMs: Number(0),
                    favorite: favoriteChannelIds.has(channel.id),
                    muted: mutedStreamIds.has(channel.id),
                } satisfies ChannelMenuItem
            })
            .sort((a, b) => {
                if (a.muted !== b.muted) {
                    return a.muted ? 1 : -1
                }
                return a.channel.label.localeCompare(b.channel.label)
            })
    }, [
        channels,
        joinedChannels,
        spaceMentions,
        unreadChannelIds,
        favoriteChannelIds,
        mutedStreamIds,
    ])

    // - - - - - - - - - - - - - - - - - - - - - - - - - collect and map all dms

    const dmItemsRef = useRef<DMChannelMenuItem[]>([])
    const dmItems = useMemo(() => {
        const value = Array.from(_dmChannels)
            .filter(
                (c) => !c.left && (!spaceId || c.userIds.every((m) => spaceMemberIds.includes(m))),
            )
            .map((channel) => {
                return {
                    type: 'dm',
                    id: channel.id,
                    label: channel.properties?.name ?? '',
                    search: channel.userIds
                        // build search names imperatively
                        .map((u) => namesFromUserId(u, { spaceId, channelId: channel.id }))
                        .join(),
                    channel,
                    unread: _dmUnreadChannelIds.has(channel.id),
                    isGroup: channel.isGdm,
                    latestMs: Number(channel?.lastEventCreatedAtEpochMs ?? 0),
                    favorite: favoriteChannelIds.has(channel.id),
                    muted: mutedStreamIds.has(channel.id),
                } satisfies DMChannelMenuItem
            })

        dmItemsRef.current = isEqual(value, dmItemsRef.current) ? dmItemsRef.current : value

        return dmItemsRef.current
    }, [
        _dmChannels,
        _dmUnreadChannelIds,
        favoriteChannelIds,
        mutedStreamIds,
        spaceId,
        spaceMemberIds,
    ])

    const unreadIdsSetRef = useRef<Set<string>>(new Set())

    const unreadIdsSet = useMemo(() => {
        const unreadChannelIdList = channelItems.filter((c) => c.unread).map((c) => c.id)
        const unreadDmIdList = dmItems.filter((c) => c.unread).map((c) => c.id)
        const value = new Set([...unreadChannelIdList, ...unreadDmIdList])

        unreadIdsSetRef.current = isEqual(value, unreadIdsSetRef.current)
            ? unreadIdsSetRef.current
            : value

        return unreadIdsSetRef.current
    }, [channelItems, dmItems])

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    // if the current route has become unread->read, we still want to keep it in
    // the menu as unread until the user navigates away from it

    const prevUnreads = useRef<string[]>([])

    const persistUnreadId =
        currentRouteId && prevUnreads.current.includes(currentRouteId) ? currentRouteId : undefined

    // - - - - - - - - - - - - - - - - - - - - - - - - - collect unread channels and dms

    const sortedUnreadChannelsRef = useRef<MixedChannelMenuItem[]>([])
    const sortedUnreadChannels = useMemo(() => {
        const value = [
            ...channelItems.filter((c) => c.unread || c.channel.id === persistUnreadId),
            ...dmItems.filter((c) => c.unread || c.channel.id === persistUnreadId),
        ].sort(
            firstBy<MixedChannelMenuItem, number | boolean>(
                (c) => (c.type === 'channel' ? c.mentionCount > 0 : c.unread),
                'desc',
            ).thenBy((c) => c.type === 'channel'),
        )

        sortedUnreadChannelsRef.current = isEqual(value, sortedUnreadChannelsRef.current)
            ? sortedUnreadChannelsRef.current
            : value

        return sortedUnreadChannelsRef.current
    }, [channelItems, dmItems, persistUnreadId])

    const sortedSpaceChannelsRef = useRef<ChannelMenuItem[]>([])
    const sortedSpaceChannels = useMemo(() => {
        const channels = [
            ...channelItems
                .filter(
                    (c) =>
                        !c.unread &&
                        persistUnreadId !== c.id &&
                        joinedChannels.has(c.id) &&
                        !c.favorite,
                )
                .sort(firstBy((c) => c.mentionCount > 0, 'desc')),
        ]
        sortedSpaceChannelsRef.current = isEqual(channels, sortedSpaceChannelsRef.current)
            ? sortedSpaceChannelsRef.current
            : channels
        return sortedSpaceChannelsRef.current
    }, [channelItems, joinedChannels, persistUnreadId])

    const favoriteChannelListRef = useRef<(ChannelMenuItem | DMChannelMenuItem)[]>([])
    const sortedFavoriteChannels = useMemo(() => {
        const value = [
            ...channelItems.filter((c) => c.favorite && !c.unread && persistUnreadId !== c.id),
            ...dmItems.filter((c) => c.favorite && !c.unread && persistUnreadId !== c.id),
        ]

        favoriteChannelListRef.current = isEqual(value, favoriteChannelListRef.current)
            ? favoriteChannelListRef.current
            : value
        return favoriteChannelListRef.current
    }, [channelItems, dmItems, persistUnreadId])

    // - - - - - - - - - - - - - - - - - - - - - collect read dms sorted by date

    const sortedDmChannelsRef = useRef<DMChannelMenuItem[]>([])
    const sortedDmChannels = useMemo(() => {
        const value = [...dmItems.filter((c) => !c.favorite && !unreadIdsSet.has(c.id))].sort(
            (a, b) => Math.sign(b.latestMs - a.latestMs),
        )
        sortedDmChannelsRef.current = isEqual(value, sortedDmChannelsRef.current)
            ? sortedDmChannelsRef.current
            : value
        return sortedDmChannelsRef.current
    }, [dmItems, unreadIdsSet])

    const unjoinedChannels = useMemo(() => {
        return channelItems.filter((c) => !c.joined)
    }, [channelItems])

    prevUnreads.current = useMemo(() => {
        const value = sortedUnreadChannels.map((u) => u.id)
        return isEqual(value, prevUnreads.current) ? prevUnreads.current : value
    }, [sortedUnreadChannels])

    return {
        channelItems,
        dmItems,
        sortedDmChannels,
        sortedFavoriteChannels,
        sortedSpaceChannels,
        sortedUnreadChannels,
        spaceMemberIds,
        spaceMentions,
        unjoinedChannels,
    }
}

export const useJoinedChannels = (spaceId: string | undefined) => {
    const space = useSpaceDataWithId(spaceId, 'useJoinedChannels')
    const myChannelGroups = useMyChannels(space)
    const joinedChannels = useMemo(
        () => new Set(myChannelGroups.flatMap((g) => g.channels).map((c) => c.id)),
        [myChannelGroups],
    )

    return {
        joinedChannels,
    }
}

const namesFromUserId = (
    userId: string,
    { spaceId, channelId }: { spaceId?: string; channelId?: string },
) => {
    const { allUsers, spaceUsers, channelUsers } = useUserLookupStore.getState()

    const displayName =
        channelUsers[channelId ?? '']?.[userId]?.displayName ||
        (spaceId
            ? spaceUsers[spaceId ?? '']?.[userId]?.displayName
            : // match all users when searching outside a space
              Object.values(allUsers[userId] ?? [])
                  .filter((s) => s.displayName)
                  .map((s) => s.displayName)
                  .join())

    const userName =
        channelUsers[channelId ?? '']?.[userId]?.username ||
        (spaceId
            ? spaceUsers[spaceId ?? '']?.[userId]?.username
            : // match all users when searching outside a space
              Object.values(allUsers[userId] ?? [])
                  .filter((s) => s.username)
                  .map((s) => s.username)
                  .join())

    return [displayName, userName].filter(Boolean).join(' ')
}
